import { Component, EASE_IN_QUINT, element as $, elements as $$, ITweezer } from '@contorion/core';

const SELECTOR_CHECKLIST_ITEMS: string = '.js-password-checklist-item';
const SELECTOR_STRENGTH: string = '.js-password-strength';
const SELECTOR_STRENGTH_BAR: string = '.js-password-strength-bar';
const CLASS_VALID: string = 'valid';
const CLASS_INVALID: string = 'invalid';
const CLASS_NONE: string = 'strength--none';
const CLASS_QUARTER: string = 'strength--quarter';
const CLASS_THREE_QUARTER: string = 'strength--three-quarter';
const CLASS_FULL: string = 'strength--full';
const DURATION_FILL_ANIMATION: number = 500;
const PROPERTY_ANIMATION: string = 'width';

export class PasswordHelpComponent extends Component {
    protected tweezer: ITweezer;
    protected tweezerJs: any;

    protected get passwordField(): HTMLInputElement {
        const targetSelector: string = (<HTMLElement> this.element).dataset.for;

        return <HTMLInputElement> $(targetSelector);
    }

    protected get checklistItems(): Array<HTMLElement> {
        return $$<HTMLElement>(SELECTOR_CHECKLIST_ITEMS, this.element);
    }

    protected get password(): string {
        return this.passwordField.value;
    }

    protected get strengthBar(): HTMLElement {
        return <HTMLElement> $(SELECTOR_STRENGTH_BAR, this.element);
    }

    protected get validCount(): number {
        return $$(`.${CLASS_VALID}`, this.element).length;
    }

    protected get strengthContainer(): HTMLElement {
        return <HTMLElement> $(SELECTOR_STRENGTH, this.element);
    }

    constructor(element: HTMLInputElement) {
        super(element);
        this.init();

        this.passwordField.addEventListener('keyup', this.onInputValChange.bind(this));
        this.passwordField.addEventListener('focus', this.onInputFocus.bind(this));
    }

    protected async init(): Promise<void> {
        this.tweezerJs = await import (/* webpackChunkName: "tweezer.js" */ 'tweezer.js');
    }

    protected onInputValChange(): void {
        this.render();
    }

    protected onInputFocus(): void {
        this.render();
    }

    protected render(): void {
        this.validateCheckList();
        this.renderStrengthBar();
    }

    protected validateCheckList(): void {
        for (let checklistItem of this.checklistItems) {
            if (this.password === '') {
                this.markItemNeutral(checklistItem);

                continue;
            }

            const regrex: RegExp = new RegExp(checklistItem.dataset.exp);

            if (regrex.test(this.password)) {
                this.markItemValid(checklistItem);

                continue;
            }

            this.markItemInvalid(checklistItem);
        }
    }

    protected markItemValid(item: HTMLElement): void {
        item.classList.add(CLASS_VALID);
        item.classList.remove(CLASS_INVALID);
    }

    protected markItemInvalid(item: HTMLElement): void {
        item.classList.remove(CLASS_VALID);
        item.classList.add(CLASS_INVALID);
    }

    protected markItemNeutral(item: HTMLElement): void {
        item.classList.remove(CLASS_VALID);
        item.classList.remove(CLASS_INVALID);
    }

    protected renderStrengthBar(): void {
        let strength: number = this.validCount / this.checklistItems.length * 100;
        const startWidth: number = parseInt(
            this.strengthBar.style.getPropertyValue(PROPERTY_ANIMATION),
            10
        );

        strength = strength === 50 ? 25 : strength;

        this.tweezer = new this.tweezerJs.default({
            duration: DURATION_FILL_ANIMATION,
            easing: EASE_IN_QUINT,
            end: strength,
            start: startWidth,
        })
            .on('tick', (v: number) => {
                this.strengthBar.style.setProperty(PROPERTY_ANIMATION, `${v}%`);
            })
            .on('done', () => {
                this.updateStrengthClasses(strength);
            })
            .begin();

    }

    protected updateStrengthClasses(strength: number): void {
        this.strengthContainer.classList.remove(CLASS_NONE);
        this.strengthContainer.classList.remove(CLASS_QUARTER);
        this.strengthContainer.classList.remove(CLASS_THREE_QUARTER);
        this.strengthContainer.classList.remove(CLASS_FULL);

        switch (true) {
            case strength === 0:
                this.strengthContainer.classList.add(CLASS_NONE);

                break;
            case strength <= 25:
                this.strengthContainer.classList.add(CLASS_QUARTER);

                break;
            case strength <= 75:
                this.strengthContainer.classList.add(CLASS_THREE_QUARTER);

                break;
            default:
                this.strengthContainer.classList.add(CLASS_FULL);

                break;
        }
    }
}
